export enum Priority {
  RED = 'RED',
  YELLOW = 'YELLOW',
  BLUE = 'BLUE'
}

export enum TaskStatus {
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  CLARIFIED = 'clarified',
  COMPLETED = 'completed'
}

export interface Evidence {
  message_id: string;
  quote: string;
  attribute: string;
}

export interface Task {
  id: string;
  title: string;
  owner: string | null;
  deadline_iso: string | null;
  priority_band: Priority;
  priority_score: number;
  why_chips: string[];
  clarify_question: string | null;
  source_link: string | null;
  doc_chips: Array<{ title: string; url: string }>;
  confidence: number;
  flags: string[];
  status: TaskStatus;
  created_at: string;
  source_messages: string[];
  evidence?: Evidence[];
}

// New flat API response structure (matches backend InboxItemResponse)
export interface InboxItem {
  // Core task data
  task_id: string;
  title: string;
  priority_band: Priority;
  priority_score: number;
  status: TaskStatus;

  // Normalized fields (backend-computed)
  display_owner: string | null;  // Friendly name
  owner_email: string | null;    // Stable identifier
  summary: string;               // Human-readable summary
  why_it_matters: string;        // ≤8 words from priority engine
  suggested_action: string;      // "Assign owner", "Clarify scope", etc.
  thread_preview: string;        // First message snippet

  // Tri-meter scores
  urgency_score: number | null;
  impact_score: number;          // Same as priority_score
  confidence_score: number;

  // Supporting data
  why_chips: string[];
  flags: string[];
  deadline_iso: string | null;
  source_link: string | null;
  clarify_question: string | null;
  created_at: string;
}

export interface InterruptTrend {
  date: string;  // ISO date string
  count: number;
}

export interface InterruptMetrics {
  user: string;
  count: number;
  daily_avg: number;
  trend: string;  // "rising", "falling", "stable"
  interrupt_trends: InterruptTrend[];  // Daily history
  avg_minutes_between: number | null;  // Avg time between interrupts
  hot_zone_score: number;  // 0-1, >0.7 = high interrupt zone
  response_latency_minutes: number | null;  // Median time to acknowledge
  deep_work_blocks: number;  // Count of uninterrupted >2hr periods
}

export interface RecurringTheme {
  label: string;
  count: number;
  example_tasks?: string[];
}

export interface AttentionSnapshot {
  window_days: number;
  attention_tax_pct: number;
  total_interrupts: number;
  interrupts_per_person: InterruptMetrics[];
  band_mix: {
    RED: number;
    YELLOW: number;
    BLUE: number;
  };
  recurring_themes: RecurringTheme[];
  hot_zone_users: string[];
  hero_stat: string;
  focus_protected_pct: number;  // % of team with <4 interrupts/day
  avg_team_minutes_between: number | null;  // Team-wide avg
  total_deep_work_blocks: number;  // Sum across team
  median_response_time_minutes: number | null;  // Team median
  generated_at?: string;
}

// Demo Scenario Types
export interface DemoScenario {
  key: string;
  name: string;
  description: string;
  priority_mix: {
    RED: number;
    YELLOW: number;
    BLUE: number;
  };
}

export interface DemoSeedResult {
  success: boolean;
  scenario: string;
  description: string;
  messages_processed: number;
  actionable_messages: number;
  tasks_extracted: number;
  tasks_saved: number;
  priority_breakdown: {
    RED: number;
    YELLOW: number;
    BLUE: number;
  };
  expected_priority_mix?: {
    RED: number;
    YELLOW: number;
    BLUE: number;
  };
}